"use client";

/**
 * Drawer 07 — "Run your own math." Shared, single implementation of the
 * interactive calculator used by both /v3/a and /v3/b (identical behavior;
 * each variation's own stylesheet just needs to include CALC_CSS, or wrap
 * this in whatever container it wants — the calculator's own classNames
 * are self-contained under `.v3calc`).
 *
 * Round 7 — COST OF DOING NOTHING. This used to end on a two-sided compare:
 * "per client by hand $X vs per client with Kreayt $100", a bar pair, and an
 * N× cheaper ratio line. Price left the homepage entirely (Jay's call; it
 * lives on /pilot now, and the first pilots are free), so asserting a rate
 * here would contradict the page. What remains is the half that was never
 * ours to assert anyway: what covering the book by hand already costs, from
 * the visitor's own four inputs. No Kreayt figure, no ratio, no pilot anchor.
 *
 * Formulas:
 *   conversations = advisors × clientsPerAdvisor
 *   hours         = conversations × prepMinutes / 60
 *   advisorDays   = hours / 8
 *   handValue     = hours × hourlyRate
 *   perClientHand = prepMinutes / 60 × hourlyRate
 *
 * Degenerate inputs (any field zero, negative or unparseable) still fall
 * through to the prompt line rather than rendering $0 figures.
 */

import { useMemo, useState } from "react";
import {
  CALCULATOR,
  CALCULATOR_VERDICT,
  CALCULATOR_VERDICT_DEGENERATE,
} from "./content";

type FieldKey = "advisors" | "clientsPerAdvisor" | "prepMinutes" | "hourlyRate";

const FIELD_ORDER: FieldKey[] = [
  "advisors",
  "clientsPerAdvisor",
  "prepMinutes",
  "hourlyRate",
];

// The time input is featured on its own row (Round 6); the rest sit in a grid.
const GRID_FIELDS: FieldKey[] = ["advisors", "clientsPerAdvisor", "hourlyRate"];

const money = (n: number) =>
  `$${Math.round(n).toLocaleString("en-US")}`;

const num = (n: number, maxFractionDigits = 0) =>
  n.toLocaleString("en-US", { maximumFractionDigits: maxFractionDigits });

export function Calculator() {
  const [values, setValues] = useState<Record<FieldKey, string>>({
    advisors: String(CALCULATOR.inputs.advisors.defaultValue),
    clientsPerAdvisor: String(CALCULATOR.inputs.clientsPerAdvisor.defaultValue),
    prepMinutes: String(CALCULATOR.inputs.prepMinutes.defaultValue),
    hourlyRate: String(CALCULATOR.inputs.hourlyRate.defaultValue),
  });

  const parsed = useMemo(() => {
    const n: Record<FieldKey, number> = {
      advisors: parseFloat(values.advisors),
      clientsPerAdvisor: parseFloat(values.clientsPerAdvisor),
      prepMinutes: parseFloat(values.prepMinutes),
      hourlyRate: parseFloat(values.hourlyRate),
    };
    return n;
  }, [values]);

  const isDegenerate = FIELD_ORDER.some(
    (k) => !isFinite(parsed[k]) || parsed[k] <= 0
  );

  // The two promoted figures: what one client's quarter costs in advisor
  // time, and what the whole book costs at that rate.
  const perClientHand = isDegenerate
    ? 0
    : (parsed.prepMinutes / 60) * parsed.hourlyRate;

  const conversations = isDegenerate
    ? 0
    : parsed.advisors * parsed.clientsPerAdvisor;
  const hours = isDegenerate ? 0 : (conversations * parsed.prepMinutes) / 60;
  const advisorDays = hours / 8;
  const handValue = hours * (isDegenerate ? 0 : parsed.hourlyRate);

  const handleChange = (key: FieldKey) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setValues((v) => ({ ...v, [key]: e.target.value }));
  };

  return (
    <div className="v3calc">
      <div className="calc-head">
        <div className="calc-label">{CALCULATOR.label}</div>
        <h2 className="calc-h2">{CALCULATOR.h2}</h2>
        <p className="calc-sub">{CALCULATOR.sub}</p>
        <p className="calc-hint">{CALCULATOR.editHint}</p>
      </div>

      {/* Featured time input: presets beside it, caption + explainer below. */}
      <div className="calc-timefield">
        <span className="calc-field-label">
          {CALCULATOR.inputs.prepMinutes.label}
        </span>
        <div className="calc-timerow">
          <input
            className="calc-field-input"
            type="number"
            inputMode="decimal"
            min={0}
            step="1"
            value={values.prepMinutes}
            onChange={handleChange("prepMinutes")}
            aria-label={CALCULATOR.inputs.prepMinutes.label}
          />
          <div className="calc-presets">
            {CALCULATOR.prepPresets.map((preset) => {
              const on = values.prepMinutes === String(preset.value);
              return (
                <button
                  type="button"
                  key={preset.label}
                  className={on ? "calc-chip on" : "calc-chip"}
                  aria-pressed={on}
                  onClick={() =>
                    setValues((v) => ({ ...v, prepMinutes: String(preset.value) }))
                  }
                >
                  {preset.label}
                </button>
              );
            })}
          </div>
        </div>
        <p className="calc-timecaption">{CALCULATOR.prepCaption}</p>
        <details className="calc-timedetails">
          <summary>{CALCULATOR.prepDisclosure.summary}</summary>
          <p>{CALCULATOR.prepDisclosure.body}</p>
        </details>
      </div>

      <div className="calc-inputs">
        {GRID_FIELDS.map((key) => (
          <label className="calc-field" key={key}>
            <span className="calc-field-label">
              {CALCULATOR.inputs[key].label}
            </span>
            <input
              className="calc-field-input"
              type="number"
              inputMode="decimal"
              min={0}
              step="1"
              value={values[key]}
              onChange={handleChange(key)}
              aria-label={CALCULATOR.inputs[key].label}
            />
          </label>
        ))}
      </div>

      {!isDegenerate ? (
        <>
          <div className="calc-hero">
            <div className="calc-hero-side">
              <span className="calc-hero-label">
                {CALCULATOR.perClient.handLabel}
              </span>
              <span className="calc-hero-value">{money(perClientHand)}</span>
            </div>
            <div className="calc-hero-side">
              <span className="calc-hero-label">
                {CALCULATOR.firmTotalLabel}
              </span>
              <span className="calc-hero-value">{money(handValue)}</span>
            </div>
          </div>

          <div className="calc-firm">
            <span className="calc-firm-lead">{CALCULATOR.firmLead}</span>
            <span className="calc-firm-item">
              {CALCULATOR.outputs.conversations} <b>{num(conversations)}</b>
            </span>
            <span className="calc-firm-item">
              {CALCULATOR.outputs.advisorDays} <b>{num(advisorDays, 1)}</b>
            </span>
          </div>

          <p className="calc-verdict">{CALCULATOR_VERDICT}</p>
        </>
      ) : (
        <p className="calc-verdict">{CALCULATOR_VERDICT_DEGENERATE}</p>
      )}

      <p className="calc-fineprint">{CALCULATOR.finePrint}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CALC_CSS — shared styling for the calculator, used identically by both
// variations. The input affordance rules (border/hover/focus) are the
// explicit UX correction from Jay: fields must read as editable at a
// glance, not as static stat tiles.
// ---------------------------------------------------------------------------
export const CALC_CSS = `
  .v3calc { max-width:820px; margin:0 auto; }
  .v3calc .calc-head { text-align:center; margin-bottom:30px; }
  .v3calc .calc-label { font-family:var(--mono); font-size:11px; letter-spacing:2px; text-transform:uppercase; color:#9C7A3C; font-weight:700; margin-bottom:10px; }
  .v3calc .calc-h2 { font-family:var(--serif-memo); font-size:clamp(22px,2.6vw,30px); font-weight:600; letter-spacing:-.4px; color:#14202C; }
  .v3calc .calc-sub { color:#55607a; font-size:14px; max-width:56ch; margin:12px auto 0; }
  .v3calc .calc-hint { font-family:var(--mono); font-size:11.5px; color:#9C7A3C; margin-top:14px; }

  /* Featured time input (Round 6): presets beside it, caption + disclosure below. */
  .v3calc .calc-timefield { margin-bottom:20px; }
  .v3calc .calc-timefield .calc-field-label { display:block; margin-bottom:8px; }
  .v3calc .calc-timerow { display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
  .v3calc .calc-timerow .calc-field-input { flex:0 0 140px; width:140px; }
  .v3calc .calc-presets { display:flex; gap:8px; flex-wrap:wrap; }
  .v3calc .calc-chip { font-family:var(--mono); font-size:10.5px; letter-spacing:.4px; text-transform:uppercase; color:#55607a; background:#fff; border:1px solid #C9BEA6; border-radius:999px; padding:7px 12px; cursor:pointer; transition:background-color .15s, border-color .15s, color .15s; }
  .v3calc .calc-chip:hover { border-color:#9C7A3C; color:#16324F; }
  .v3calc .calc-chip.on { background:#16324F; border-color:#16324F; color:#F4F1E9; }
  .v3calc .calc-timecaption { font-family:var(--mono); font-size:10.5px; letter-spacing:.4px; color:#9AA1AB; margin-top:8px; }
  .v3calc .calc-timedetails { margin-top:8px; }
  .v3calc .calc-timedetails summary { font-size:12px; color:#9C7A3C; cursor:pointer; font-weight:600; list-style:none; display:inline-flex; align-items:center; gap:6px; }
  .v3calc .calc-timedetails summary::-webkit-details-marker { display:none; }
  .v3calc .calc-timedetails summary::before { content:"＋"; font-family:var(--mono); font-size:11px; }
  .v3calc .calc-timedetails[open] summary::before { content:"－"; }
  .v3calc .calc-timedetails p { font-size:12.5px; color:#55607a; line-height:1.6; margin-top:10px; max-width:64ch; }

  .v3calc .calc-inputs { display:grid; grid-template-columns:repeat(3,1fr); gap:14px; margin-bottom:34px; }
  @media(max-width:640px){ .v3calc .calc-inputs{ grid-template-columns:1fr; } }
  .v3calc .calc-field { display:flex; flex-direction:column; gap:8px; }
  .v3calc .calc-field-label { font-size:11.5px; color:#55607a; font-weight:600; letter-spacing:.2px; }
  .v3calc .calc-field-input {
    font-family: var(--serif-memo);
    font-variant-numeric: tabular-nums;
    font-size: 20px;
    font-weight: 600;
    color: #14202C;
    background: #FDFCF9;
    border: 1.5px solid #C9BEA6;
    border-radius: 3px;
    padding: 11px 14px;
    min-height: 46px;
    width: 100%;
    cursor: text;
    transition: border-color .15s, box-shadow .15s;
  }
  .v3calc .calc-field-input:hover { border-color:#9C7A3C; }
  .v3calc .calc-field-input:focus {
    outline: none;
    border-color: #16324F;
    box-shadow: 0 0 0 3px #16324F22;
  }
  /* Hide native number-input spinners on WebKit so the boxed field reads
     as a clean editable figure, not a form control — the border/hover/
     focus states above already carry the "this is editable" signal. */
  .v3calc .calc-field-input::-webkit-outer-spin-button,
  .v3calc .calc-field-input::-webkit-inner-spin-button { opacity: 0.6; }

  /* The two promoted figures (Round 7): one client's quarter, then the whole
     book. Both are the SAME quantity at different scale — advisor time — so
     they share one neutral treatment. The old green "with Kreayt" side is
     gone along with price; nothing here should read as a second party's
     number to compare against. */
  .v3calc .calc-hero { display:flex; align-items:stretch; justify-content:center; border:1px solid #E6E1D6; background:#fff; margin-bottom:24px; }
  .v3calc .calc-hero-side { flex:1; padding:22px 18px; display:flex; flex-direction:column; gap:8px; text-align:center; }
  .v3calc .calc-hero-side + .calc-hero-side { border-left:1px solid #E6E1D6; }
  .v3calc .calc-hero-label { font-size:11.5px; color:#55607a; line-height:1.35; }
  .v3calc .calc-hero-value { font-family:var(--serif-memo); font-variant-numeric:tabular-nums; font-size:clamp(30px,5vw,44px); font-weight:700; color:#16324F; letter-spacing:-.5px; line-height:1; }
  @media(max-width:520px){
    .v3calc .calc-hero { flex-direction:column; }
    .v3calc .calc-hero-side + .calc-hero-side { border-left:none; border-top:1px solid #E6E1D6; }
  }

  /* Firm-level counts, demoted to a small caption row. */
  .v3calc .calc-firm { display:flex; flex-wrap:wrap; align-items:baseline; justify-content:center; gap:6px 18px; padding:14px 16px; border-top:1px solid #E6E1D6; }
  .v3calc .calc-firm-lead { width:100%; text-align:center; font-family:var(--mono); font-size:10px; letter-spacing:1.2px; text-transform:uppercase; color:#9AA1AB; font-weight:700; }
  .v3calc .calc-firm-item { font-size:11.5px; color:#9AA1AB; }
  .v3calc .calc-firm-item b { font-family:var(--serif-memo); font-variant-numeric:tabular-nums; color:#55607a; font-weight:700; }

  .v3calc .calc-verdict { font-family:var(--serif-memo); font-size:16.5px; line-height:1.55; color:#3a4658; max-width:70ch; margin:0 auto 12px; text-align:center; }
  .v3calc .calc-verdict strong { color:#16324F; }
  .v3calc .calc-fineprint { font-size:11px; color:#9AA1AB; text-align:center; }
`;
